import React from 'react';

import { ui } from '../..';
import * as enums from '../../enums';
import { mui } from '../../libs';
import * as types from '../../types';

interface IReturnsClippingControlsProps {
  returnHorizon: types.returnHorizon.ReturnHorizon;
  // eslint-disable-next-line no-unused-vars
  onUpdate: (updatedRh: types.returnHorizon.ReturnHorizon) => void;
}

/**
 * Component for managing returns clipping controls including toggle and bounds configuration
 *
 * @param returnHorizon - The return horizon object to modify
 * @param onUpdate - Callback function to handle updates to the return horizon
 */
const ReturnsClippingControls: React.FC<IReturnsClippingControlsProps> = ({ returnHorizon, onUpdate }) => {
  const isClippingEnabled = returnHorizon?.with_returns_clipping ?? true;

  const handleToggleClipping = () => {
    const updatedRh = {
      ...returnHorizon,
      with_returns_clipping: !isClippingEnabled,
      clipping_lower_bound: !isClippingEnabled ? (returnHorizon.clipping_lower_bound ?? -0.25) : null,
      clipping_upper_bound: !isClippingEnabled ? (returnHorizon.clipping_upper_bound ?? 0.25) : null,
    };
    onUpdate(updatedRh);
  };

  const handleLowerBoundChange = (value: number | null) => {
    const updatedRh = { ...returnHorizon, clipping_lower_bound: value };
    onUpdate(updatedRh);
  };

  const handleUpperBoundChange = (value: number | null) => {
    const updatedRh = { ...returnHorizon, clipping_upper_bound: value };
    onUpdate(updatedRh);
  };

  return (
    <mui.core.Box mt={3}>
      <mui.core.FormControlLabel
        control={<mui.core.Switch size="small" checked={isClippingEnabled} onChange={handleToggleClipping} />}
        label="Enable Returns Clipping"
      />

      {isClippingEnabled && (
        <>
          <mui.core.Typography
            variant="body2"
            fontWeight="fontWeightMedium"
            sx={{
              marginTop: '16px',
              marginBottom: '10px',
            }}
          >
            Returns Clipping Bounds
          </mui.core.Typography>
          <mui.core.Grid container spacing={2}>
            <mui.core.Grid size={{ xs: 6 }}>
              <ui.NumericField
                textFieldProps={{
                  label: 'Lower Bound',
                  fullWidth: true,
                  variant: 'outlined',
                  size: 'small',
                }}
                value={returnHorizon.clipping_lower_bound ?? -0.25}
                setValue={handleLowerBoundChange}
                suffix={'%'}
                dividerOption={enums.NUMBER_FORMATTERS_ENUM.HUNDRED}
              />
            </mui.core.Grid>

            <mui.core.Grid size={{ xs: 6 }}>
              <ui.NumericField
                textFieldProps={{
                  label: 'Upper Bound',
                  fullWidth: true,
                  variant: 'outlined',
                  size: 'small',
                }}
                value={returnHorizon.clipping_upper_bound ?? 0.25}
                setValue={handleUpperBoundChange}
                suffix={'%'}
                dividerOption={enums.NUMBER_FORMATTERS_ENUM.HUNDRED}
                onlyPositive={true}
              />
            </mui.core.Grid>
          </mui.core.Grid>
        </>
      )}
    </mui.core.Box>
  );
};

export default ReturnsClippingControls;
